import { ColumnDef, sortingFns } from '@tanstack/react-table';
import { versionSortingFn } from 'src/components/GenericTable/sortingFn';
import React, { useMemo, useState } from 'react';
import { buildLibraryCostColumn } from 'src/components/ControlCost';
import { useFrameworkControlsCostFields } from 'src/components/ControlCost';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from '../types';
import { Link } from 'react-router-dom';
import { gTCheckHandler } from '../util';
import { MPEvents } from 'src/utils/mixpanel/types';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TextOverflowTooltip } from 'src/common';
import { ControlFrameworkDetailsQuery, useControlsRmpCheckQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { useAddControlsWithVariableReview } from 'src/components/modals/MissingVariablesModal';
import * as S from './useControlLibraryColumns.styles';

type ControlLibraryItem = ControlFrameworkDetailsQuery['controlFrameworks'][number]['frameworkControls'][number];

type UseColumns = (a: {
  data?: ControlLibraryItem[];
  isCheckerEnabled?: boolean;
  opensSameTab?: boolean;
  tdm?: {
    onAddToRMPClick?: boolean;
  };
  tableId: TableIds;
  /** Fires after an add-to-RMP actually completes — NOT when the Missing-variables
   * review opens instead; page-side completion UI (selection labels etc.) must live
   * here, never in a `.then()` on `addToRmp`. */
  onAddSuccess?: () => void;
}) => {
  columns: ColumnDef<ControlLibraryItem>[];
  checked: string[];
  setChecked: (a: string[]) => void;
  GTColumnSelectAnchorExported: JSX.Element;
  addToRmp: (ids: string[]) => Promise<void>;
  isAddLoading: boolean;
  /** Missing-variables review for the add-to-RMP paths — consumers that call
   * `addToRmp` (or enable the row action) must render this. */
  addReviewModal: JSX.Element | null;
};

const defaultColumns = ['framework_controls', 'framework_control_id', 'name', 'FrameworkAuthor'];

export const useControlLibraryColumns: UseColumns = ({
  data = [],
  isCheckerEnabled,
  tdm,
  opensSameTab,
  tableId,
  onAddSuccess,
}) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${tableId}-columns`, defaultColumns);
  const [checked, setChecked] = useState<string[]>([]);
  const { refetchQuery } = useCache();
  const costFields = useFrameworkControlsCostFields(data);

  const { data: { controls: rmpControls = [] } = {} } = useControlsRmpCheckQuery();

  const {
    requestAdd,
    reviewModal: addReviewModal,
    isAdding: isAddLoading,
  } = useAddControlsWithVariableReview({
    afterSuccess: async (count) => {
      await refetchQuery({ include: ['controlsRmpCheck'] });
      showSuccessToast(count === 1 ? toastMessages.control.addedToRmp : toastMessages.control.addedToRmpPlural);
      setChecked([]);
      onAddSuccess?.();
    },
  });

  const addToRmp = async (ids: string[]) => {
    await requestAdd(ids);
  };

  const onAddToRMPClick = async (id: string) => {
    await requestAdd([id], { addRecommendedModifiers: false });
  };

  const columns = useMemo(() => {
    let internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

    if (isCheckerEnabled) {
      internalIds.push(GTInternalIds.checker);
    } else {
      internalIds.filter((el) => el !== GTInternalIds.checker);
      if (checked.length > 0) setChecked([]);
    }

    const list: ColumnDef<ControlLibraryItem>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const rows = table.getRowModel().rows;
          const headChecked =
            rows
              .map((row) => row.original.id)
              .every((id) =>
                rmpControls.some((item) => (item.frameworkControls || []).some((fc) => fc?.id === id)),
              ) && !!rows.length;

          const { isAllChecked, checkAllHandler } = gTCheckHandler(
            {
              checked,
              selector: 'id',
              table,
            },
            setChecked,
          );

          return (
            <GTCheckBox isChecked={isAllChecked || headChecked} disabled={headChecked} onClick={checkAllHandler} />
          );
        },
        cell: ({ row }) => {
          const id = row.original.id;
          const isInRmp = rmpControls.some((item) =>
            (item.frameworkControls || []).some((fc) => fc?.id === row.original.id),
          );

          return (
            <GTCheckBox
              isChecked={checked.includes(id) || isInRmp}
              disabled={isInRmp}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        accessorFn: (row) => row.controlFramework?.shortname,
        cell: ({ row }) => row.original.controlFramework?.shortname,
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_control_id',
        header: 'Framework Control ID',
        accessorFn: (row) => row.control_identifier,
        cell: ({ row }) => row.original.control_identifier,
        sortingFn: versionSortingFn,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/control-frameworks/all-controls/${info.row.original.id}`}
              target={opensSameTab ? '' : '_blank'}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Framework'],
                  },
                  destination: {
                    value: ['Framework Controls all'],
                    params: {
                      id: info.row.original.id,
                    },
                  },
                  tags: ['FRAMEWORK'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'FrameworkAuthor',
        header: 'Author',
        accessorFn: (row) => row.controlFramework?.author,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      buildLibraryCostColumn<ControlLibraryItem>({
        id: 'annual_cost',
        header: 'Annual Cost',
        field: 'annualCostFormula',
        costFields,
        getId: (row) => row.id,
      }),
      buildLibraryCostColumn<ControlLibraryItem>({
        id: 'implementation_cost',
        header: 'Implementation Cost',
        field: 'implementationCostFormula',
        costFields,
        getId: (row) => row.id,
      }),
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const isInRmp = rmpControls.some((item) =>
            (item.frameworkControls || []).some((fc) => fc?.id === row.original.id),
          );
          return (
            <S.TdmActionsRow>
              <GTTdm
                onAddToRMPClick={tdm?.onAddToRMPClick && !isInRmp ? () => onAddToRMPClick(row.original.id) : undefined}
              />
            </S.TdmActionsRow>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, rmpControls, isCheckerEnabled, checked, tdm, costFields]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={tableId}
      options={[
        { key: 'framework_controls', title: 'Framework' },
        { key: 'framework_control_id', title: 'Framework Control ID' },
        { key: 'name', title: 'Name' },
        { key: 'FrameworkAuthor', title: 'Author' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'implementation_cost', title: 'Implementation Cost' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, checked, setChecked, GTColumnSelectAnchorExported, addToRmp, isAddLoading, addReviewModal };
};
